"use client";

import React, { useState, useCallback } from "react";
import { SearchAutocomplete } from "@/components/common/sidebar/SearchAutocomplete";
import { countryFlage } from "./countryFlage";
import styles from "./countryToolFormat.module.css";
import commonStyles from "@/styles/component-style/common.module.scss";

interface CountryDetail {
  name?: string;
  countryCode?: string;
  countryCurrencies?: string[];
  alpha2?: string;
  lang?: string[];
}

const CountryToolFormat: React.FC = () => {
  const countries = require("country-data").countries;
  const currencies = require("country-data").currencies;

  const [valueOfFilterData, setValueOfFilterData] = useState<string | undefined>(undefined);
  const [storeCountryDetail, setStoreCountryDetail] = useState<CountryDetail>({});

  const handleOnSelect = useCallback((item: CountryDetail) => {
    setStoreCountryDetail(item);
    const alpha2 = item.alpha2;
    if (alpha2 && countryFlage[alpha2 as keyof typeof countryFlage]) {
      setValueOfFilterData(countryFlage[alpha2 as keyof typeof countryFlage]?.image);
    } else {
      setValueOfFilterData(undefined);
    }
  }, []);

  const SelectValue = storeCountryDetail?.countryCurrencies?.toString()?.toUpperCase();
  const match = currencies.all.find((item: { code: string }) => item.code === SelectValue);

  const formatResult = useCallback((item: CountryDetail) => (
    <span className={commonStyles.autocompleteResultItem}>{item.name}</span>
  ), []);

  const hasCountry = Boolean(storeCountryDetail?.name);

  return (
    <div className={styles.wrapper}>
      <div className={`lenghth-box-design ${styles.searchCard}`}>
        <label className={styles.searchLabel} htmlFor="country-search">
          Search for a country
        </label>
        <div className={styles.searchInputWrap}>
          <SearchAutocomplete<CountryDetail & { name: string }>
            placeholder="Type country name (e.g. United States, India)"
            items={countries.all.map((item: any, index: number) => ({
              name: item.name || `Unknown-${index}`,
              countryCode: item.countryCallingCodes?.[0],
              countryCurrencies: item.currencies,
              alpha2: item.alpha2,
              lang: item.languages,
            }))}
            onSearch={() => {
              setStoreCountryDetail({});
              setValueOfFilterData(undefined);
            }}
            onSelect={handleOnSelect}
            formatResult={formatResult}
            maxResults={50}
          />
        </div>
      </div>

      {hasCountry ? (
        <div className={styles.resultCard}>
          <div className={styles.resultHeader}>
            <h2 className={styles.resultTitle}>
              {storeCountryDetail.name}
              {storeCountryDetail.alpha2 && (
                <span className={styles.resultSub}> ({storeCountryDetail.alpha2})</span>
              )}
            </h2>
            <p className={styles.resultSub}>Country information</p>
          </div>

          <div className={`${styles.contentGrid} ${!valueOfFilterData ? styles.contentGridNoFlag : ""}`}>
            {valueOfFilterData && (
              <div className={styles.flagWrap}>
                <img
                  src={valueOfFilterData}
                  alt={`${storeCountryDetail.name} flag`}
                  className={styles.flagImg}
                />
              </div>
            )}

            <div className={styles.infoList}>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Country name</span>
                <p className={styles.infoValue}>{storeCountryDetail.name ?? "—"}</p>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Country code</span>
                <p className={storeCountryDetail.countryCode ? styles.infoValue : styles.infoValueEmpty}>
                  {storeCountryDetail.countryCode ?? "—"}
                </p>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Currencies</span>
                <p className={styles.infoValue}>
                  {storeCountryDetail.countryCurrencies?.length
                    ? `${storeCountryDetail.countryCurrencies.join(", ").toUpperCase()}${match?.name ? ` (${match.name})` : ""}`
                    : "—"}
                </p>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Currency symbol</span>
                <p className={match?.symbol ? styles.infoValue : styles.infoValueEmpty}>
                  {match?.symbol ?? "—"}
                </p>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Languages</span>
                <p className={storeCountryDetail.lang?.length ? styles.infoValue : styles.infoValueEmpty}>
                  {storeCountryDetail.lang?.join(", ").toUpperCase() ?? "—"}
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.emptyState}>
          <p className={styles.emptyTitle}>No country selected</p>
          <p className={styles.emptyText}>
            Search and select a country above to view its information, including flag, calling code, currencies, and languages.
          </p>
        </div>
      )}
    </div>
  );
};

export default CountryToolFormat;
